import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Plus, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { fetchAll } from "@/lib/fetchAll";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DataTable } from "@/components/data-table/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/_app/content/news/")({ component: NewsIndex });

interface NewsRow {
  id: number;
  slug: string;
  title: string;
  category: string | null;
  status: "draft" | "published" | "archived";
  published_at: number | null;
  updated_at: number;
}

function NewsIndex() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["news", "list"],
    queryFn: () => fetchAll<NewsRow>("/admin/news"),
  });
  const publish = useMutation({
    mutationFn: (id: number) => api.post(`/admin/news/${id}/publish`),
    onSuccess: () => { toast.success("Published"); qc.invalidateQueries({ queryKey: ["news"] }); },
  });
  const unpublish = useMutation({
    mutationFn: (id: number) => api.post(`/admin/news/${id}/unpublish`),
    onSuccess: () => { toast.success("Unpublished"); qc.invalidateQueries({ queryKey: ["news"] }); },
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/news/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["news"] }); },
  });

  const columns: ColumnDef<NewsRow, unknown>[] = [
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => (
        <Link to="/content/news/$id" as never params={{ id: String(row.original.id) }} className="font-medium hover:text-primary">
          {row.original.title}
          <span className="ml-2 text-xs text-muted-foreground">/{row.original.slug}</span>
        </Link>
      ),
    },
    { accessorKey: "category", header: "Category", cell: ({ row }) => row.original.category || <span className="text-muted-foreground">—</span> },
    { accessorKey: "status", header: "Status", cell: ({ row }) => <StatusBadge status={row.original.status} />, filterFn: (r, _i, v) => r.getValue("status") === v },
    { accessorKey: "published_at", header: "Published", cell: ({ row }) => row.original.published_at ? format(row.original.published_at * 1000, "MMM d, yyyy") : <span className="text-muted-foreground">—</span> },
    {
      id: "actions", header: "",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem asChild><Link to="/content/news/$id" as never params={{ id: String(row.original.id) }}>Edit</Link></DropdownMenuItem>
            <DropdownMenuSeparator />
            {row.original.status === "published" ? (
              <DropdownMenuItem disabled={!can("news", "publish")} onClick={() => unpublish.mutate(row.original.id)}>
                <EyeOff className="h-3.5 w-3.5" /> Unpublish
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem disabled={!can("news", "publish")} onClick={() => publish.mutate(row.original.id)}>
                <Eye className="h-3.5 w-3.5" /> Publish
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!can("news", "delete")}
              onClick={() => { if (confirm(`Delete "${row.original.title}"?`)) remove.mutate(row.original.id); }}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      <PageHeader
        title="News"
        description="Press releases, college announcements, and campus stories."
        actions={can("news", "create") && (
          <Link to="/content/news/new" as never>
            <Button><Plus className="h-4 w-4" /> New article</Button>
          </Link>
        )}
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        globalSearch
        globalSearchPlaceholder="Search by title or slug…"
        filters={[{ id: "status", label: "Status", options: [
          { value: "draft", label: "Draft" }, { value: "published", label: "Published" }, { value: "archived", label: "Archived" },
        ]}]}
        emptyMessage={isLoading ? "Loading…" : "No news yet."}
      />
    </div>
  );
}
